import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import axios from 'axios'

import { apiClient } from '../lib/api/client'
import { AuthResponse, AuthUser, DashboardUserRole, login as loginRequest, loginWithGoogle as googleLoginRequest, getCurrentUser, refreshToken as refreshTokenRequest, logout as logoutRequest } from '../lib/api/auth'
import { normalizeMeasurementUnit } from '../lib/measurements'
import { syncSentryUser } from '../lib/sentry'

type AuthContextValue = {
  user: AuthUser | null
  token: string | null
  loading: boolean
  rememberedEmail: string | null
  login: (email: string, password: string, remember?: boolean) => Promise<AuthResponse>
  loginWithGoogle: (idToken: string, remember?: boolean, password?: string) => Promise<AuthResponse>
  logout: () => void
  updateUser: (user: AuthUser) => void
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

const STORAGE_KEY = 'hikestrong-dashboard-auth'
const EMAIL_STORAGE_KEY = 'hikestrong-dashboard-email'
const REFRESH_TOKEN_KEY = 'hikestrong-dashboard-refresh'

type StoredAuth = {
  token: string
  user: AuthUser
  expiresAt?: string
}

const THIRTY_DAYS_MS = 1000 * 60 * 60 * 24 * 30

const isApiConnectionError = (error: unknown) => {
  if (!axios.isAxiosError(error)) {
    return false
  }

  return !error.response
}

const normalizeRole = (role?: AuthUser['role']): DashboardUserRole => {
  if (typeof role === 'number') {
    switch (role) {
      case 2:
        return 'Admin'
      case 1:
        return 'Editor'
      case 3:
        return 'TourAdmin'
      default:
        return 'Guest'
    }
  }

  switch (role?.toString().toLowerCase()) {
    case 'admin':
      return 'Admin'
    case 'editor':
      return 'Editor'
    case 'touradmin':
      return 'TourAdmin'
    case 'supportagent':
      return 'SupportAgent'
    case 'supportadmin':
      return 'SupportAdmin'
    default:
      return 'Guest'
  }
}

const normalizeUser = (user: AuthUser): AuthUser => ({
  ...user,
  role: normalizeRole(user.role),
  operatorId: user.operatorId ?? null,
  measurementUnit: normalizeMeasurementUnit(user.measurementUnit)
})

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [rememberedEmail, setRememberedEmail] = useState<string | null>(null)

  useEffect(() => {
    const storedEmail = window.localStorage.getItem(EMAIL_STORAGE_KEY)
    if (storedEmail) {
      setRememberedEmail(storedEmail)
    }

    const validateAndRestoreAuth = async () => {
      const stored = window.localStorage.getItem(STORAGE_KEY)
      const storedRefreshToken = window.localStorage.getItem(REFRESH_TOKEN_KEY)

      if (!stored) {
        setLoading(false)
        return
      }

      try {
        const parsed = JSON.parse(stored) as StoredAuth

        // Check frontend expiration first
        if (parsed.expiresAt && new Date(parsed.expiresAt).getTime() < Date.now()) {
          window.localStorage.removeItem(STORAGE_KEY)
          window.localStorage.removeItem(REFRESH_TOKEN_KEY)
          setLoading(false)
          return
        }

        // Set token in header for validation request
        apiClient.defaults.headers.common.Authorization = `Bearer ${parsed.token}`

        // Validate token with backend
        try {
          const backendUser = await getCurrentUser()
          const normalizedUser = normalizeUser(backendUser)
          setUser(normalizedUser)
          setToken(parsed.token)

          // Update stored user with fresh data from backend
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
            token: parsed.token,
            user: normalizedUser,
            expiresAt: parsed.expiresAt
          }))
        } catch (validationError) {
          if (isApiConnectionError(validationError)) {
            const normalizedUser = normalizeUser(parsed.user)
            setUser(normalizedUser)
            setToken(parsed.token)
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
              token: parsed.token,
              user: normalizedUser,
              expiresAt: parsed.expiresAt
            }))
            console.warn('Could not reach API while restoring auth; keeping stored session', validationError)
            setLoading(false)
            return
          }

          // Access token is invalid - try refresh token
          if (storedRefreshToken) {
            try {
              const response = await refreshTokenRequest(storedRefreshToken)
              const normalizedUser = normalizeUser(response.user)
              setUser(normalizedUser)
              setToken(response.token)
              apiClient.defaults.headers.common.Authorization = `Bearer ${response.token}`

              // Store new tokens
              window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
                token: response.token,
                user: normalizedUser,
                expiresAt: new Date(Date.now() + THIRTY_DAYS_MS).toISOString()
              }))
              if (response.refreshToken) {
                window.localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken)
              }
            } catch (refreshError) {
              // Refresh token also invalid - clear everything
              console.warn('Refresh token failed, clearing auth', refreshError)
              window.localStorage.removeItem(STORAGE_KEY)
              window.localStorage.removeItem(REFRESH_TOKEN_KEY)
              delete apiClient.defaults.headers.common.Authorization
            }
          } else {
            // No refresh token - clear auth
            console.warn('Token validation failed, no refresh token available', validationError)
            window.localStorage.removeItem(STORAGE_KEY)
            delete apiClient.defaults.headers.common.Authorization
          }
        }
      } catch (error) {
        console.warn('Failed to parse stored auth payload', error)
        window.localStorage.removeItem(STORAGE_KEY)
        window.localStorage.removeItem(REFRESH_TOKEN_KEY)
      }

      setLoading(false)
    }

    validateAndRestoreAuth()
  }, [])

  useEffect(() => {
    if (token) {
      apiClient.defaults.headers.common.Authorization = `Bearer ${token}`
    } else {
      delete apiClient.defaults.headers.common.Authorization
    }
  }, [token])

  useEffect(() => {
    syncSentryUser(user)
  }, [user])

  const persistAuth = useCallback((payload: StoredAuth | null) => {
    if (!payload) {
      window.localStorage.removeItem(STORAGE_KEY)
      return
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  }, [])

  const updateUser = useCallback((nextUser: AuthUser) => {
    const normalizedUser = normalizeUser(nextUser)
    setUser(normalizedUser)

    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      return
    }

    try {
      const parsed = JSON.parse(stored) as StoredAuth
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...parsed,
        user: normalizedUser
      }))
    } catch (error) {
      console.warn('Failed to update stored auth user', error)
    }
  }, [])

  const acceptLogin = useCallback((response: AuthResponse, remember: boolean) => {
    const normalizedUser = normalizeUser(response.user)
    setUser(normalizedUser)
    setToken(response.token)
    if (remember) {
      persistAuth({
        token: response.token,
        user: normalizedUser,
        expiresAt: new Date(Date.now() + THIRTY_DAYS_MS).toISOString()
      })
      if (response.refreshToken) {
        window.localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken)
      }
      window.localStorage.setItem(EMAIL_STORAGE_KEY, normalizedUser.email)
      setRememberedEmail(normalizedUser.email)
    } else {
      persistAuth(null)
      window.localStorage.removeItem(REFRESH_TOKEN_KEY)
      window.localStorage.removeItem(EMAIL_STORAGE_KEY)
      setRememberedEmail(null)
    }
    return { ...response, user: normalizedUser }
  }, [persistAuth])

  const login = useCallback(async (email: string, password: string, remember = false) => {
    setLoading(true)
    try {
      const response = await loginRequest({ email, password, rememberMe: remember })
      return acceptLogin(response, remember)
    } finally {
      setLoading(false)
    }
  }, [acceptLogin])

  const loginWithGoogle = useCallback(async (idToken: string, remember = true, password?: string) => {
    setLoading(true)
    try {
      const response = await googleLoginRequest({ idToken, rememberMe: remember, ...(password !== undefined ? { password } : {}) })
      return acceptLogin(response, remember)
    } finally {
      setLoading(false)
    }
  }, [acceptLogin])

  const logout = useCallback(async () => {
    // Revoke refresh token on backend
    const storedRefreshToken = window.localStorage.getItem(REFRESH_TOKEN_KEY)
    try {
      await logoutRequest(storedRefreshToken ?? undefined)
    } catch (error) {
      // Ignore logout errors - still clear local state
      console.warn('Logout request failed', error)
    }

    setUser(null)
    setToken(null)
    persistAuth(null)
    window.localStorage.removeItem(REFRESH_TOKEN_KEY)
  }, [persistAuth])

  const value = useMemo(
    () => ({ user, token, loading, rememberedEmail, login, loginWithGoogle, logout, updateUser }),
    [user, token, loading, rememberedEmail, login, loginWithGoogle, logout, updateUser]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
